/**
 * MeetingSummary — the slideout summary card for `ahoy_meeting` records.
 *
 * Composes:
 *   - Attendees pill list (shared `MeetingAttendees`).
 *   - Optional meeting link (ahoy_meeting_link, falling back to
 *     ahoy_location when it looks like a URL).
 *
 * Registered on `ENTITY_TYPE_REGISTRY.ahoy_meeting.components.summary`. The
 * slideout's SummarySection consults that slot before falling back to its
 * legacy switch; consumers should never import this component directly.
 */

import { Link03 } from '@untitledui/icons';

import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { MeetingAttendees } from './meeting-attendees';

export function MeetingSummary({ object }: { object: CrmObject }) {
  // Meeting link — prefer ahoy_meeting_link, fall back to ahoy_location when
  // it's a URL. Trim before protocol-checking / href-building so
  // whitespace-padded property values don't produce broken `href`s.
  const explicitMeetLink = getPropertyValue<string>(
    object.properties?.ahoy_meeting_link
  )?.trim();
  const location = getPropertyValue<string>(
    object.properties?.ahoy_location
  )?.trim();
  const meetLink =
    explicitMeetLink ||
    (location && /^https?:\/\//i.test(location) ? location : undefined);
  const showMeetingLink = !!meetLink;

  return (
    <div className='space-y-5'>
      {/* Attendees */}
      <MeetingAttendees meeting={object} />

      {/* Meeting Link */}
      {showMeetingLink && (
        <div>
          <p className='text-xs md:text-sm font-medium text-[var(--color-text-primary)] mb-2.5'>
            Meeting link
          </p>
          <div className='flex items-center gap-2'>
            <Link03
              className='w-3.5 h-3.5 text-fg-quaternary flex-shrink-0'
              aria-hidden='true'
            />
            <a
              href={
                /^https?:\/\//i.test(meetLink)
                  ? meetLink
                  : `https://${meetLink}`
              }
              target='_blank'
              rel='noopener noreferrer'
              className='text-xs md:text-sm text-fg-brand-primary hover:underline truncate'
            >
              {meetLink}
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
